import {
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { ReactNode } from "react";

interface SearchInputProps {
  searchKey: string;
  onSearch: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  name?: string;
  // endIcon?: JSX.Element;
  startIcon?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * @param searchKey - The current search key
 * @param onSearch - The function to call when the search key changes
 * @param onClear - The function to call when the clear button is clicked
 * @param placeholder - The placeholder text for the input
 * @param name - The name of the input
 *
 * @returns A text field with a clear button
 *
 * @example
 * <SearchInput
 *  searchKey={searchKey}
 *  onSearch={onSearch}
 *  onClear={onClear}
 *  placeholder="Search"
 *  name="search"
 * />
 *
 * @author [Rajkumar Rathod]
 */
const SearchInput = ({
  searchKey,
  onSearch,
  onClear: _onClear,
  placeholder,
  name,
  // endIcon,
  startIcon,
  sx,
}: SearchInputProps) => {
  const handleClear = () => {
    onSearch?.("");
    _onClear?.();
  };

  return (
    <TextField
      placeholder={placeholder}
      fullWidth
      variant="outlined"
      name={name}
      value={searchKey ?? ""}
      onChange={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onSearch?.(e.target.value ?? "");
      }}
      InputProps={{
        sx: { "& .MuiInputBase-input": { fontSize: "15px" } },
        startAdornment: (
          <InputAdornment position="start">
            {startIcon ?? (
              <img
                src="/search.svg"
                alt=""
                style={{ filter: "brightness(0)", width: 16, height: 16 }}
              />
            )}
          </InputAdornment>
        ),
        endAdornment: searchKey ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="Clear search"
              size="small"
              edge="end"
              onClick={handleClear}
              sx={{ padding: "2px" }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: 26,
          borderRadius: "4px",
          backgroundColor: "#FAFAFA",
          fontSize: 12,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#CCC",
          },
        },
        ...(sx as object),
      }}
    />
  );
};

export default SearchInput;
